import { Injectable } from '@angular/core';
import { Http, blobToFile } from 'src/app/shared';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { TranslationService } from './translation.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class Excel {
  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });
  
  constructor(
    private http: Http,
    private translationService: TranslationService,
  ) {}

  async export(exportRequest: ExportRequest, keysToTranslate?: string[], fileNameSuffix?: string) {
    // Use current language so export respects translations when language is changed
    this.translationRequest.language = sessionStorage.getItem('languagepreference');

    const headersToTranslate = exportRequest.sheets[0].headers;
    
    // Collect all translation promises: headers, fileName, and row values
    const allTranslationPromises: Promise<any>[] = [];
    
    // Header translation promises
    const headerPromises = headersToTranslate.map(header => 
      firstValueFrom(
        this.translationService.getTranslationForKey$(header.label, this.translationRequest)
      )
    );
    allTranslationPromises.push(...headerPromises);

    // FileName translation promise
    const fileNamePromise = firstValueFrom(
      this.translationService.getTranslationForKey$(exportRequest.fileName, this.translationRequest)
    );
    allTranslationPromises.push(fileNamePromise);

    // Row value translation promises for specific keys
    const rowTranslationData: Array<{sheetIndex: number, rowIndex: number, key: string, promise: Promise<string>}> = [];
    
    exportRequest.sheets.forEach((sheet, sheetIndex) => {
      if (sheet.rows && keysToTranslate) {
        sheet.rows.forEach((row: any, rowIndex: number) => {
          keysToTranslate?.forEach(key => {
            if (row[key] != null && row[key] !== undefined && row[key] !== '') {
              const translationPromise = firstValueFrom(
                this.translationService.getTranslationForKey$(row[key], this.translationRequest)
              ).catch(() => row[key]); // If translation fails, keep original value
              rowTranslationData.push({ sheetIndex, rowIndex, key, promise: translationPromise });
              allTranslationPromises.push(translationPromise);
            }
          });
        });
      }
    });

    // Wait for all translations to complete
    await Promise.all(allTranslationPromises);

    // Reconstruct translated headers (promises are already resolved)
    const translatedHeaders = await Promise.all(headerPromises);
    headersToTranslate.forEach((header, index) => {
      header.label = translatedHeaders[index];
    });

    const fileName = await fileNamePromise ?? exportRequest.fileName;
    // Reconstruct translated fileName (promise is already resolved)
    exportRequest.fileName = `${fileName}${fileNameSuffix ? (fileNameSuffix) : ''}`;

    // Reconstruct translated rows (promises are already resolved)
    for (const { sheetIndex, rowIndex, key, promise } of rowTranslationData) {
      try {
        const translatedValue = await promise;
        exportRequest.sheets[sheetIndex].rows[rowIndex][key] = translatedValue;
      } catch (error) {
        // If translation fails, keep original value (no action needed)
      }
    }
    
    exportRequest.sheets[0].headers = headersToTranslate;
    const content = await this.http.post<Blob>(
      '/api/excel/export',
      exportRequest,
      { responseType: 'blob' }
    );
    blobToFile(
      content as Blob,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      exportRequest.fileName,
    );
  }
}

export class ExportRequest {
  fileName: string;
  sheets: ExportSheet[] = [];
  details: ExportHeader[] = [];
}

export class ExportSheet {
  name: string;
  rows: any[];
  headers: ExportHeader[];
}

export class ExportHeader {
  label: string;
  value?: string;

  // values: date,first,last,header
  format?: ExportHeaderFormat;
  translationNeeded?: boolean;
}

type ExportHeaderFormat = 'date' | 'first' | 'last' | 'header';
